import { useEffect, useRef, useCallback, useState } from 'react';
import { useFrameworkWizardStore } from '../states';
import { uploadFileReq } from '@/shared/requests/upload-file';
import { addNotification } from '@/shared/states/notification';

export const useStepThreeAutoSave = () => {
  const { stepTwoData, stepThreeData } = useFrameworkWizardStore();
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to upload controls data using backend-provided URL directly
  const uploadControlsData = useCallback(async (controlsData?: any[]) => {
    if (!stepTwoData.controlsUrl) {
      throw new Error('Controls URL not found');
    }

    // Use provided data or get cleaned controls from store
    const dataToUpload = controlsData || (stepThreeData.getCleanedControls ? stepThreeData.getCleanedControls() : stepThreeData.controls);
    
    if (!dataToUpload || dataToUpload.length === 0) {
      return;
    }

    // Create JSON blob
    const controlsBlob = new Blob([JSON.stringify(dataToUpload, null, 2)], {
      type: 'application/json'
    });

    // Upload directly to the backend-provided URL (now has RWD permissions)
    await uploadFileReq(stepTwoData.controlsUrl, controlsBlob);
  }, [stepTwoData.controlsUrl, stepThreeData.getCleanedControls, stepThreeData.controls]);

  // Debounced auto-save function
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      if (!stepTwoData.controlsUrl || !stepThreeData.controls) {
        return;
      }

      try {
        setIsSaving(true);
        await uploadControlsData();
      } catch (error) {
        console.error('Auto-save failed:', error);
        // Don't show error notifications for auto-save failures
      } finally {
        setIsSaving(false);
      }
    }, 2000); // 2 second delay
  }, [stepTwoData.controlsUrl, stepThreeData.controls, uploadControlsData]);

  // Manual save function
  const saveNow = useCallback(async (dataToSave?: any[]) => {
    if (!stepTwoData.controlsUrl) {
      throw new Error('Controls URL not found');
    }

    try {
      setIsSaving(true);
      await uploadControlsData(dataToSave);
    } catch (error) {
      console.error('Manual save failed:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [stepTwoData.controlsUrl, uploadControlsData]);

  // Auto-save when controls data changes
  useEffect(() => {
    if (stepThreeData.controls && stepThreeData.controls.length > 0) {
      debouncedSave();
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [stepThreeData.controls, debouncedSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    saveNow,
    isSaving
  };
};
